import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const AdminContainer = styled.div`
  min-height: 100vh;
  background-color: #0f172a;
  color: #f8fafc;
  padding: 40px 20px;
  font-family: 'Inter', sans-serif;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  border-bottom: 1px solid #334155;
  padding-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #38bdf8;
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
`;

const StatCard = styled.div`
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const StatValue = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  color: #f8fafc;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TableContainer = styled.div`
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  background: #0f172a;
  color: #94a3b8;
  padding: 16px;
  text-align: left;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #334155;
`;

const Td = styled.td`
  padding: 16px;
  border-bottom: 1px solid #334155;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const Tr = styled.tr`
  &:hover {
    background: #334155;
  }
`;

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #0f172a;
`;

const LoginForm = styled.form`
  background: #1e293b;
  padding: 40px;
  border-radius: 12px;
  border: 1px solid #334155;
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 8px;
  color: white;
  outline: none;
  &:focus {
    border-color: #38bdf8;
  }
`;

const Button = styled.button`
  background: #0ea5e9;
  color: white;
  border: none;
  padding: 12px;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #0284c7;
  }
`;

const API_URL = window.location.hostname.includes('urvanshshah.netlify.app')
  ? 'https://portfolio-ai-backend-sr2m.onrender.com/api'
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    localStorage.getItem('adminAuth') === 'true'
  );
  const [password, setPassword] = useState(localStorage.getItem('adminAuthToken') || '');
  const [logs, setLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [analytics, setAnalytics] = useState({ totalChats: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password.trim()) {
      setIsAuthenticated(true);
      localStorage.setItem('adminAuth', 'true');
      localStorage.setItem('adminAuthToken', password);
    }
  };

  const handleLogout = useCallback(() => {
    setIsAuthenticated(false);
    setPassword('');
    localStorage.removeItem('adminAuth');
    localStorage.removeItem('adminAuthToken');
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = {
        'Authorization': `Bearer ${password}`
      };
      const [historyRes, analyticsRes] = await Promise.all([
        fetch(`${API_URL}/chat-history`, { headers }),
        fetch(`${API_URL}/analytics`, { headers })
      ]);
      
      if (historyRes.status === 401 || analyticsRes.status === 401) {
        handleLogout();
        throw new Error('Unauthorized. Invalid admin password.');
      }
      if (!historyRes.ok || !analyticsRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const historyData = await historyRes.json();
      const analyticsData = await analyticsRes.json();

      setLogs(historyData);
      setAnalytics(analyticsData);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not connect to backend API. Please make sure the server is running.');
    } finally {
      setLoading(false);
    }
  }, [password, handleLogout]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  const filteredLogs = logs.filter(log => 
    (log.user_message && log.user_message.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (log.ai_response && log.ai_response.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (log.session_id && log.session_id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (!isAuthenticated) {
    return (
      <LoginContainer>
        <LoginForm onSubmit={handleLogin}>
          <h2 style={{color: 'white', margin: 0, textAlign: 'center'}}>Admin Portal</h2>
          <Input 
            type="password" 
            placeholder="Enter Admin Password" 
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          <Button type="submit">Login</Button>
        </LoginForm>
      </LoginContainer>
    );
  }

  return (
    <AdminContainer>
      <ContentWrapper>
        <Header>
          <Title>AI Chatbot Analytics</Title>
          <div>
            <Button onClick={fetchData} style={{ marginRight: 10, background: '#475569' }}>Refresh</Button>
            <Button onClick={handleLogout} style={{ background: '#ef4444' }}>Logout</Button>
          </div>
        </Header>

        {error && (
            <div style={{ background: 'rgba(239, 68, 68, 0.2)', border: '1px solid #ef4444', padding: 16, borderRadius: 8, marginBottom: 20, color: '#fca5a5' }}>
                {error}
            </div>
        )}

        <StatsGrid>
          <StatCard>
            <StatValue>{analytics.totalChats}</StatValue>
            <StatLabel>Total Interactions</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{logs.length}</StatValue>
            <StatLabel>Recent Logs Tracked</StatLabel>
          </StatCard>
        </StatsGrid>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{color: '#e2e8f0', margin: 0}}>Recent Conversations</h2>
          <Input 
            type="text" 
            placeholder="Search logs..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '300px' }}
          />
        </div>

        <TableContainer>
          <Table>
            <thead>
              <Tr>
                <Th>Time</Th>
                <Th>User ID / IP</Th>
                <Th style={{width: '30%'}}>User Query</Th>
                <Th style={{width: '40%'}}>AI Response</Th>
              </Tr>
            </thead>
            <tbody>
              {loading ? (
                <Tr><Td colSpan={4} style={{textAlign: 'center', padding: '16px'}}>Loading...</Td></Tr>
              ) : filteredLogs.length === 0 ? (
                <Tr><Td colSpan={4} style={{textAlign: 'center', padding: '16px'}}>No chat logs found.</Td></Tr>
              ) : (
                filteredLogs.map(log => (
                  <Tr key={log.id}>
                    <Td>{new Date(log.created_at).toLocaleString()}</Td>
                    <Td>
                      <div style={{fontSize: '0.8rem', color: '#94a3b8'}}>{log.session_id?.substring(0,8)}...</div>
                      <div style={{fontSize: '0.75rem', color: '#64748b'}}>{log.ip_address}</div>
                    </Td>
                    <Td style={{color: '#38bdf8'}}>{log.user_message}</Td>
                    <Td>
                      <div style={{maxHeight: '100px', overflowY: 'auto', paddingRight: '10px', whiteSpace: 'pre-wrap', fontSize: '0.8rem'}}>
                        {log.ai_response}
                      </div>
                    </Td>
                  </Tr>
                ))
              )}
            </tbody>
          </Table>
        </TableContainer>

      </ContentWrapper>
    </AdminContainer>
  );
};

export default AdminDashboard;
